import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Row, Col, Card, Form, Table, Button, Badge, Spinner, ProgressBar, Tabs, Tab } from "react-bootstrap";
import { Bar } from "react-chartjs-2";
import Select from "react-select"; 
import axios from "axios";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import API_ENDPOINTS from "../../config/api"; 
import { 
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, 
  Title, Tooltip, Legend, LineElement, PointElement, LineController 
} from 'chart.js';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement, 
  PointElement, LineController, Title, Tooltip, Legend
);

const chartLabelsPlugin = {
  id: 'chartLabelsPlugin',
  afterDatasetsDraw(chart) {
    const { ctx, data } = chart;
    ctx.save();
    data.datasets.forEach((dataset, i) => {
      if (dataset.label === "Performance (%)" || dataset.label === "KPI Progress (%)") {
        const meta = chart.getDatasetMeta(i);
        meta.data.forEach((bar, index) => {
          const value = dataset.data[index] + "%";
          ctx.fillStyle = "#576574";
          ctx.font = "bold 10px Arial";
          ctx.textAlign = "left";
          ctx.textBaseline = "middle";
          ctx.fillText(value, bar.x + 5, bar.y);
        });
      }
    });
    ctx.restore();
  }
};

const DailyDashboard = () => {
  const [reports, setReports] = useState([]);
  const [kpis, setKpis] = useState([]); 
  const [departments, setDepartments] = useState([]); 
  const [employees, setEmployees] = useState([]);      
  const [expandedDepts, setExpandedDepts] = useState({});
  const [expandedSched, setExpandedSched] = useState({});
  const [expandedKpis, setExpandedKpis] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("task-status");
  
  const today = new Date().toISOString().split('T')[0];
  const [filters, setFilters] = useState({
    startDate: today, endDate: today, selectedUser: null, departmentId: ""
  });

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
  };

  const handleExportExcel = async () => {
    if (reports.length === 0 && kpis.length === 0) return alert("No data to export!");
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Daily Report");

    const headerStyle = {
      font: { bold: true, color: { argb: "FFFFFF" } },
      fill: { type: "pattern", pattern: "solid", fgColor: { argb: "4A69BD" } },
      alignment: { horizontal: "center", vertical: "middle" },
      border: { top: {style:'thin'}, left: {style:'thin'}, bottom: {style:'thin'}, right: {style:'thin'} }
    };

    const groupRowStyle = {
      font: { bold: true },
      fill: { type: "pattern", pattern: "solid", fgColor: { argb: "F8F9FA" } },
      border: { top: {style:'thin'}, left: {style:'thin'}, bottom: {style:'thin'}, right: {style:'thin'} }
    };

    const borderStyle = { top: {style:'thin'}, left: {style:'thin'}, bottom: {style:'thin'}, right: {style:'thin'} };

    const drawTaskTableGrouped = (title, groupedData) => {
      const titleRow = worksheet.addRow([title]);
      titleRow.font = { bold: true, size: 12 };
      worksheet.mergeCells(`A${titleRow.number}:G${titleRow.number}`);
      
      const hRow = worksheet.addRow(["#", "PIC", "PROJECT", "MAIN JOB", "DETAIL", "DATE", "HOURS", "STATUS"]);
      hRow.eachCell(cell => { cell.style = headerStyle; });

      Object.keys(groupedData).forEach(dept => {
        const gRow = worksheet.addRow(["▶", dept.toUpperCase()]);
        worksheet.mergeCells(`B${gRow.number}:H${gRow.number}`);
        gRow.eachCell(cell => { cell.style = groupRowStyle; });

        groupedData[dept].forEach((item, idx) => {
          const row = worksheet.addRow([
            idx + 1,
            item.fullName,
            item.projectName,
            item.mainTask,
            item.taskDetail || item.dailyDetail,
            item.date ? new Date(item.date).toLocaleDateString('en-GB') : "",
            item.hours + "h",
            item.status
          ]);
          row.eachCell(cell => { 
            cell.border = borderStyle; 
            cell.alignment = { vertical: 'middle', wrapText: true }; 
          });
        });
      });
      worksheet.addRow([]);
    };

    const drawKpiTableGrouped = (title, groupedKpi) => {
      const titleRow = worksheet.addRow([title]);
      titleRow.font = { bold: true, size: 12 };
      worksheet.mergeCells(`A${titleRow.number}:G${titleRow.number}`);
      
      const hRow = worksheet.addRow(["#", "KPI CODE", "DESCRIPTION", "UNIT", "TARGET", "ACHIEVED", "PROGRESS"]);
      hRow.eachCell(cell => { cell.style = headerStyle; });

      Object.keys(groupedKpi).forEach(user => {
        const gRow = worksheet.addRow(["▶", user.toUpperCase()]);
        worksheet.mergeCells(`B${gRow.number}:G${gRow.number}`);
        gRow.eachCell(cell => { 
          cell.style = groupRowStyle; 
          cell.font = { bold: true, color: { argb: "007BFF" } };
        });

        groupedKpi[user].forEach((k, idx) => {
          const row = worksheet.addRow([
            idx + 1,
            k.kpiCode,
            k.description,
            k.unit,
            k.targetValue,
            k.achievedValue,
            (k.progress || 0).toFixed(1) + "%"
          ]);
          row.eachCell(cell => { 
            cell.border = borderStyle; 
            cell.alignment = { vertical: 'middle', wrapText: true }; 
          });
        });
      });
    };

    const gWork = reports.filter(i => i.status !== 'Scheduling').reduce((a, i) => { const d = i.departmentName || "Unassigned"; a[d] = a[d] || []; a[d].push(i); return a; }, {});
    const gSched = reports.filter(i => i.status === 'Scheduling').reduce((a, i) => { const d = i.departmentName || "Unassigned"; a[d] = a[d] || []; a[d].push(i); return a; }, {});
    const gKpis = kpis.reduce((acc, k) => { const uName = k.fullName || "Unassigned User"; acc[uName] = acc[uName] || []; acc[uName].push(k); return acc; }, {});

    worksheet.columns = [
        { width: 5 }, { width: 25 }, { width: 25 }, { width: 40 }, 
        { width: 15 }, { width: 10 }, { width: 15 }
    ];

    drawTaskTableGrouped("I. CHI TIẾT CÔNG VIỆC THỰC TẾ (ACTUAL REPORT)", gWork);
    drawTaskTableGrouped("II. KẾ HOẠCH SẮP TỚI (SCHEDULING)", gSched);
    drawKpiTableGrouped("III. TỔNG HỢP KPI (KPI SUMMARY)", gKpis);

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Daily_Performance_Report_${filters.startDate}.xlsx`);
  };

  const fetchReport = useCallback(async (currentFilters = filters) => {
    setLoading(true);
    try {
      const apiParams = {
        startDate: currentFilters.startDate,
        endDate: currentFilters.endDate,
        departmentId: currentFilters.departmentId,
        userId: currentFilters.selectedUser ? currentFilters.selectedUser.value : ""
      };

      const config = { headers: getAuthHeader(), params: apiParams };
      const res = await axios.get(`${API_ENDPOINTS.taskdailies}/summary-report`, config);
      const reportData = res.data.tasks || [];
      const kpiData = res.data.kpiSummary || [];
      setReports(reportData);
      setKpis(kpiData);

      const initialExpanded = {};
      reportData.forEach(item => { if (item.departmentName) initialExpanded[item.departmentName] = true; });
      setExpandedDepts(initialExpanded); setExpandedSched(initialExpanded);
      const initialKpiExpanded = {};
      kpiData.forEach(k => { if (k.fullName) initialKpiExpanded[k.fullName] = true; });
      setExpandedKpis(initialKpiExpanded);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const config = { headers: getAuthHeader() };
        const [deptRes, empRes] = await Promise.all([
          axios.get(`${API_ENDPOINTS.taskdailies}/departments`, config),
          axios.get(`${API_ENDPOINTS.taskdailies}/users`, config)
        ]);
        setDepartments(deptRes.data || []); setEmployees(empRes.data || []);
        fetchReport();
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    loadData();
  }, [fetchReport]);

  const horizontalOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    barThickness: 15,
    plugins: {
      legend: { position: 'top', labels: { boxWidth: 10, font: { size: 11 } } },
    },
    scales: {
      x: { beginAtZero: true, grid: { color: '#f1f2f6' }, ticks: { font: { size: 10 } } },
      y: { ticks: { font: { size: 11, weight: '500' } }, grid: { display: false } }
    }
  };

  const kpiOptions = {
    ...horizontalOptions,
    scales: {
      ...horizontalOptions.scales,
      x: { 
        beginAtZero: true, 
        max: 100, 
        grid: { color: '#f1f2f6' }, 
        ticks: { 
          font: { size: 10 },
          callback: (value) => value + "%" 
        } 
      }
    }
  };

  const getStatusChartData = () => {
    const dailyStats = reports.reduce((acc, item) => {
      const key = `${item.fullName}`;
      if (!acc[key]) acc[key] = { done: 0, processing: 0, pending: 0, delay: 0 };
      const s = (item.status || "").toLowerCase();
      const hrs = parseFloat(item.hours || 0);
      if (s === 'done') acc[key].done += hrs;
      else if (s === 'processing') acc[key].processing += hrs;
      else if (s === 'delay') acc[key].delay += hrs;
      else acc[key].pending += hrs;
      return acc;
    }, {});
    const labels = Object.keys(dailyStats);
    return {
      labels,
      datasets: [
        { label: "Done", data: labels.map(k => dailyStats[k].done), backgroundColor: "#2ecc71" },
        { label: "Processing", data: labels.map(k => dailyStats[k].processing), backgroundColor: "#3498db" },
        { label: "Pending", data: labels.map(k => dailyStats[k].pending), backgroundColor: "#f1c40f" },
        { label: "Delay", data: labels.map(k => dailyStats[k].delay), backgroundColor: "#e74c3c" }
      ]
    };
  };

  const getDeptPerformanceData = () => {
    const start = new Date(filters.startDate);
    const end = new Date(filters.endDate);
    const diffDays = Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24)) + 1;
    const deptMap = reports.reduce((acc, item) => {
      const dName = item.departmentName || "Others";
      if (!acc[dName]) acc[dName] = { totalHours: 0, employees: new Set() };
      acc[dName].totalHours += parseFloat(item.hours || 0);
      acc[dName].employees.add(item.fullName); 
      return acc;
    }, {});
    const labels = Object.keys(deptMap);
    return {
      labels,
      datasets: [
        { label: "Performance (%)", data: labels.map(l => {
          const cap = deptMap[l].employees.size * diffDays * 8; 
          return cap > 0 ? ((deptMap[l].totalHours / cap) * 100).toFixed(1) : 0;
        }), backgroundColor: "#4a69bd" }
      ]
    };
  };

  const getUserKpiChartData = () => {
    const userKpiMap = kpis.reduce((acc, k) => {
      const name = k.fullName || "Unknown";
      if (!acc[name]) acc[name] = { totalProgress: 0, count: 0 };
      acc[name].totalProgress += k.progress || 0;
      acc[name].count += 1;
      return acc;
    }, {});
    const labels = Object.keys(userKpiMap);
    return {
      labels,
      datasets: [
        { label: "KPI Progress (%)", data: labels.map(l => (userKpiMap[l].totalProgress / userKpiMap[l].count).toFixed(1)), backgroundColor: "#9b59b6" }
      ]
    };
  };

  const renderTableRows = (grouped, expanded, setExpanded) => {
    if (Object.keys(grouped).length === 0) return <tr><td colSpan="8" className="text-center p-3 text-muted small">No data</td></tr>;
    return Object.keys(grouped).map(dept => (
      <React.Fragment key={dept}>
        <tr onClick={() => setExpanded(p => ({...p, [dept]: !p[dept]}))} style={{cursor:'pointer', backgroundColor:'#f8f9fa'}}>
          <td className="text-center">{expanded[dept] ? '▼' : '▶'}</td>
          <td colSpan="6" className="fw-bold text-dark small">{dept.toUpperCase()}</td>
        </tr>
        {expanded[dept] && (grouped[dept] || []).map((item, idx) => (
          <tr key={idx} className="small align-middle">
            <td className="text-center text-muted">{idx + 1}</td>
            <td><strong>{item.fullName}</strong></td>
            <td>{item.projectName}</td>
            <td>{item.mainTask}</td>
            <td>{item.taskDetail || item.dailyDetail}</td>
            <td className="text-center">{item.date ? new Date(item.date).toLocaleDateString('en-GB') : ""}</td>
            <td className="text-center fw-bold">{item.hours}h</td>
            <td className="text-center"><Badge bg={item.status === 'Done' ? 'success' : (item.status === 'Delay' ? 'danger' : 'primary')}>{item.status}</Badge></td>
          </tr>
        ))}
      </React.Fragment>
    ));
  };

  const gWork = reports.filter(i => i.status !== 'Scheduling').reduce((a, i) => { const d = i.departmentName || "Unassigned"; a[d] = a[d] || []; a[d].push(i); return a; }, {});
  const gSched = reports.filter(i => i.status === 'Scheduling').reduce((a, i) => { const d = i.departmentName || "Unassigned"; a[d] = a[d] || []; a[d].push(i); return a; }, {});
  const thStyle = { backgroundColor: "#4a69bd", color: "white", border: "1px solid #dee2e6", fontSize: "0.75rem", padding: "10px" };
  
  const gKpis = kpis.reduce((acc, k) => {
    const uName = k.fullName || "Unassigned User";
    if (!acc[uName]) acc[uName] = [];
    acc[uName].push(k);
    return acc;
  }, {});

  const userOptions = useMemo(() => employees.map(u => ({
    value: u.id || u.Id,
    label: u.fullName
  })), [employees]);

  const selectStyles = {
    control: (base) => ({ 
      ...base, 
      borderRadius: "4px", 
      fontSize: "0.85rem", 
      minHeight: "31px",
      borderColor: "#dee2e6",
      boxShadow: "none"
    }),
    valueContainer: (provided) => ({ ...provided, height: '31px', padding: '0 6px' }),
    input: (provided) => ({ ...provided, margin: '0px' }),
    indicatorsContainer: (provided) => ({ ...provided, height: '31px' }),
  };

  const activeStaffCount = Object.keys(reports.reduce((a,b)=>({...a, [b.fullName]:1}),{})).length;
  const activeDeptCount = Object.keys(reports.reduce((a,b)=>({...a, [b.departmentName || "Others"]:1}),{})).length;
  const dynamicStaffHeight = Math.max(200, (activeStaffCount * 40) + 60);
  const dynamicDeptHeight = Math.max(200, (activeDeptCount * 50) + 60);

  return (
    <div className="p-4 bg-white min-vh-100">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-bold text-dark text-uppercase mb-0">Daily Performance Monitoring</h4>
        <Button variant="success" size="sm" className="fw-bold shadow-sm px-3" onClick={handleExportExcel}>EXPORT EXCEL</Button>
      </div>

      <Card className="mb-4 border-0 bg-light shadow-sm p-3">
        <Row className="g-2 align-items-end">
          <Col md={2}>
             <label className="small fw-bold text-muted">START DATE</label>
             <Form.Control type="date" size="sm" value={filters.startDate} onChange={e => setFilters({...filters, startDate: e.target.value})} />
          </Col>
          <Col md={2}>
             <label className="small fw-bold text-muted">END DATE</label>
             <Form.Control type="date" size="sm" value={filters.endDate} onChange={e => setFilters({...filters, endDate: e.target.value})} />
          </Col>
          <Col md={2}>
             <label className="small fw-bold text-muted">DEPARTMENT</label>
             <Form.Select size="sm" value={filters.departmentId} onChange={e => setFilters({...filters, departmentId: e.target.value})}>
               <option value="">-- All Dept --</option>
               {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
             </Form.Select>
          </Col>
          <Col md={4}>
            <label className="small fw-bold text-muted">FULL NAME</label>
            <Select
              isClearable
              options={userOptions}
              value={filters.selectedUser}
              onChange={(option) => setFilters({ ...filters, selectedUser: option })}
              placeholder="Search employee..."
              styles={selectStyles}
              classNamePrefix="select"
            />
          </Col>
          <Col md={2}>
            <Button variant="dark" className="w-100 fw-bold" size="sm" onClick={() => fetchReport()}>
              {loading ? <Spinner size="sm" animation="border" /> : "SEARCH"}
            </Button>
          </Col>
        </Row>
      </Card>

      <div className="mb-4">
        <h6 className="fw-bold text-dark mb-3">I. ACTUAL PROGRESS</h6>
        <Table responsive bordered hover className="mb-0 shadow-sm border-0">
          <thead><tr className="text-center align-middle"><th style={thStyle}>#</th><th style={thStyle}>PIC</th><th style={thStyle}>PROJECT</th><th style={thStyle}>MAIN JOB</th><th style={thStyle}>DETAIL</th><th style={thStyle}>DATE</th><th style={thStyle}>HOURS</th><th style={thStyle}>STATUS</th></tr></thead>
          <tbody>{renderTableRows(gWork, expandedDepts, setExpandedDepts)}</tbody>
        </Table>
      </div>

      <div className="mb-4">
        <h6 className="fw-bold text-dark mb-3">II. SCHEDULING / PLANS</h6>
        <Table responsive bordered hover className="mb-0 shadow-sm border-0">
          <thead><tr className="text-center align-middle"><th style={thStyle}>#</th><th style={thStyle}>PIC</th><th style={thStyle}>PROJECT</th><th style={thStyle}>MAIN JOB</th><th style={thStyle}>DETAIL</th><th style={thStyle}>DATE</th><th style={thStyle}>HOURS</th><th style={thStyle}>STATUS</th></tr></thead>
          <tbody>{renderTableRows(gSched, expandedSched, setExpandedSched)}</tbody>
        </Table>
      </div>

      <div className="mb-5">
        <h6 className="fw-bold text-dark mb-3">III. KPI SUMMARY</h6>
        <Table responsive bordered hover className="mb-0 shadow-sm border-0">
          <thead>
            <tr className="text-center align-middle">
              <th style={thStyle}>#</th><th style={thStyle}>KPI CODE</th><th style={thStyle}>DESCRIPTION</th><th style={thStyle}>UNIT</th><th style={thStyle}>TARGET</th><th style={thStyle}>ACHIEVED</th><th style={thStyle}>PROGRESS</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(gKpis).length > 0 ? Object.keys(gKpis).map(user => (
              <React.Fragment key={user}>
                <tr onClick={() => setExpandedKpis(p => ({...p, [user]: !p[user]}))} style={{cursor:'pointer', backgroundColor:'#eef2f7'}}>
                  <td className="text-center">{expandedKpis[user] ? '▼' : '▶'}</td>
                  <td colSpan="6" className="fw-bold text-primary small">{user.toUpperCase()}</td>
                </tr>
                {expandedKpis[user] && gKpis[user].map((k, idx) => (
                  <tr key={k.id} className="small align-middle">
                    <td className="text-center text-muted">{idx + 1}</td>
                    <td className="fw-bold text-center">{k.kpiCode}</td>
                    <td>{k.description}</td>
                    <td className="text-center">{k.unit}</td>
                    <td className="text-center fw-bold">{k.targetValue}</td>
                    <td className="text-center fw-bold text-primary">{k.achievedValue}</td>
                    <td style={{ width: '200px' }}>
                      <div className="d-flex align-items-center">
                        <ProgressBar now={k.progress} variant={k.progress >= 100 ? "success" : "info"} className="flex-grow-1 me-2" style={{ height: '10px' }} />
                        <span className="fw-bold" style={{ fontSize: '10px' }}>{k.progress.toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            )) : <tr><td colSpan="7" className="text-center p-3 text-muted small">No data</td></tr>}
          </tbody>
        </Table>
      </div>

      <div className="mb-5">
        <h6 className="fw-bold text-dark mb-3 text-uppercase">IV. Visual Analytics</h6>
        <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="custom-tabs bg-white border-bottom px-3" justify>
            <Tab eventKey="task-status" title="🕒 Task Status (Hours)">
              <div style={{ maxHeight: '600px', overflowY: 'auto', padding: '15px' }}>
                <div style={{ height: `${dynamicStaffHeight}px` }}>
                  <Bar data={getStatusChartData()} options={horizontalOptions} />
                </div>
              </div>
            </Tab>
            <Tab eventKey="dept-efficiency" title="🏢 Dept. Efficiency (%)">
              <div style={{ maxHeight: '600px', overflowY: 'auto', padding: '15px' }}>
                <div style={{ height: `${dynamicDeptHeight}px` }}>
                  <Bar data={getDeptPerformanceData()} options={horizontalOptions} plugins={[chartLabelsPlugin]} />
                </div>
              </div>
            </Tab>
            <Tab eventKey="kpi-progress" title="🎯 KPI Progress (%)">
              <div style={{ maxHeight: '600px', overflowY: 'auto', padding: '15px' }}>
                <div style={{ height: `${dynamicStaffHeight}px` }}>
                  <Bar data={getUserKpiChartData()} options={kpiOptions} plugins={[chartLabelsPlugin]} />
                </div>
              </div>
            </Tab>
          </Tabs>
        </Card>
      </div>
    </div>
  );
};

export default DailyDashboard;